import { useEffect, useRef, useCallback } from 'react';
import { oilOrdersApi, OilSupplier, OilOrderResponse } from '../api/oilOrders';

/**
 * Хук для открытия корзины НЕВА в новой вкладке после успешной отправки.
 *
 * Не открывает вкладку сразу — запоминает ожидание и открывает только
 * когда polling обнаружит status === 'sent' для web-поставщика.
 */
export function useNevaCart(suppliers: OilSupplier[]) {
  const nevaCartUrl = useRef<string | null>(null);
  // Set order_id, для которых ждём завершения отправки НЕВА
  const pendingOrderIds = useRef<Set<number>>(new Set());

  useEffect(() => {
    oilOrdersApi.getSettings().then(settings => {
      if (settings.neva_config?.url) {
        // Используем наш бэкенд-эндпоинт для авто-логина + редиректа в корзину
        nevaCartUrl.current = '/api/oil-orders/neva-redirect';
      }
    });
  }, []);

  /** Найти id web-поставщика (НЕВА) */
  const getNevaId = useCallback(() => {
    return suppliers.find(s => s.source_type === 'web')?.id ?? null;
  }, [suppliers]);

  /** Пометить заказ как ожидающий открытия НЕВА (вызывать при resend) */
  const markPending = useCallback((orderId: number, supplierId: number) => {
    const sup = suppliers.find(s => s.id === supplierId);
    if (nevaCartUrl.current && sup?.source_type === 'web') {
      pendingOrderIds.current.add(orderId);
    }
  }, [suppliers]);

  /** Пометить заказ как ожидающий, если среди items есть НЕВА (вызывать при placeOrder) */
  const markPendingIfHasNeva = useCallback((orderId: number, items: { supplier_id: number }[]) => {
    const nevaId = getNevaId();
    if (nevaCartUrl.current && nevaId && items.some(i => i.supplier_id === nevaId)) {
      pendingOrderIds.current.add(orderId);
    }
  }, [getNevaId]);

  /**
   * Проверить обновлённые заказы (из polling) — если НЕВА стала 'sent',
   * открыть вкладку и убрать из ожидания.
   */
  const checkAndOpen = useCallback((orders: OilOrderResponse[]) => {
    if (!nevaCartUrl.current || pendingOrderIds.current.size === 0) return;
    const nevaId = getNevaId();
    if (!nevaId) return;

    for (const orderId of Array.from(pendingOrderIds.current)) {
      const order = orders.find(o => o.id === orderId);
      if (!order) continue;
      const nevaEmail = order.emails?.find(e => e.supplier_id === nevaId);
      if (nevaEmail?.status === 'sent') {
        pendingOrderIds.current.delete(orderId);
        const token = localStorage.getItem('access_token') || '';
        window.open(`${nevaCartUrl.current}?token=${encodeURIComponent(token)}`, '_blank');
      } else if (nevaEmail?.status === 'error') {
        // Ошибка — не открываем, убираем из ожидания
        pendingOrderIds.current.delete(orderId);
      }
    }
  }, [getNevaId]);

  return { markPending, markPendingIfHasNeva, checkAndOpen };
}
